import Layout from "../components/Layout";
import { exportManualPDF } from "../lib/manualPdf";
import { toast } from "sonner";
import {
  FileDown,
  LayoutDashboard,
  CalendarDays,
  ClipboardList,
  ShieldCheck,
  MapPin,
  Receipt,
  AlertTriangle,
  LogIn,
  Sparkles,
  Network,
  Calculator,
  Zap,
} from "lucide-react";

const ROLES = [
  { rol: "Administrador", desc: "Visión completa del negocio. Configura, audita y supervisa todo.", modulos: "Todos" },
  { rol: "Supervisor", desc: "Coordina cuadrillas, revisa fiscalización, aprueba calidad.", modulos: "Dashboard · Agendamiento · Órdenes · Fiscalización · Mapa · NC" },
  { rol: "Técnico", desc: "Ejecuta instalaciones en campo. Solo ve sus propias órdenes.", modulos: "Sólo sus órdenes asignadas" },
  { rol: "Fiscalizador", desc: "Valida fotos y documenta no-conformidades.", modulos: "Dashboard · Órdenes · Fiscalización · Mapa · NC" },
  { rol: "Financiero", desc: "Aprueba pre-facturas y controla SLA de contratistas.", modulos: "Dashboard · Facturación" },
];

const ESTADOS = [
  { n: 1, e: "Asignada", d: "La orden ha sido creada y dada a un técnico." },
  { n: 2, e: "En Progreso", d: "El técnico está ejecutando el trabajo." },
  { n: 3, e: "Completada", d: "El trabajo terminó, las evidencias están cargadas." },
  { n: 4, e: "Validada", d: "Fiscalizador y la IA confirmaron que está bien hecho." },
  { n: 5, e: "Facturada", d: "El sistema generó la pre-factura para el contratista." },
  { n: 6, e: "Rechazada", d: "Algo salió mal y se devuelve para corrección." },
];

const MODULES = [
  {
    icon: LogIn,
    name: "Pantalla de Login",
    purpose: "Acceso al sistema con reconocimiento automático del rol.",
    who: "Todos los usuarios.",
    how: [
      "Ingresa con correo corporativo y contraseña.",
      "El sistema muestra automáticamente los módulos correspondientes a tu rol.",
      "En esta versión PMV, hay 5 cuentas demo de un clic en la pantalla de login.",
    ],
    pitch: "\"Cada miembro del equipo entra con su cuenta. El sistema sabe qué puede ver y qué puede hacer según el rol — un técnico nunca verá la facturación.\"",
  },
  {
    icon: LayoutDashboard,
    name: "Dashboard Operativo",
    purpose: "Panel ejecutivo de KPIs en tiempo real con gráficos y exportación PDF.",
    who: "Administrador, Supervisor, Fiscalizador, Financiero.",
    how: [
      "KPIs principales: Total Órdenes, Lead Time, Efectividad de Cuadrilla, Pre-facturación Pendiente.",
      "Métricas secundarias: Discrepancias GPS, No Conformidades abiertas, Asignadas hoy.",
      "Gráficos: tendencia 14 días (línea), distribución por estado (dona), volumen por tipo de solución (barras horizontales).",
      "Botón \"Exportar reporte PDF\" genera un one-pager corporativo.",
    ],
    pitch: "\"En cinco segundos cualquier ejecutivo sabe cómo va la operación: cuánta gente trabaja, qué tan rápido se cierran las órdenes, cuánto dinero está represado y dónde están los problemas.\"",
  },
  {
    icon: CalendarDays,
    name: "Agendamiento",
    purpose: "Calendario semanal con asignación de órdenes a cuadrillas.",
    who: "Administrador, Supervisor.",
    how: [
      "Cada fila es un técnico; cada columna un día de la semana.",
      "En cada celda aparecen las órdenes programadas con código, cliente y estado.",
      "Botones de navegación entre semanas; clic en una orden abre el detalle.",
    ],
    pitch: "\"El supervisor ve de un vistazo cómo está distribuida la carga semanal. Si una cuadrilla está sobrecargada y otra ociosa, lo identifica de inmediato.\"",
  },
  {
    icon: ClipboardList,
    name: "Órdenes de Trabajo",
    purpose: "Corazón operativo: lista, búsqueda, detalle y ciclo de vida de cada orden.",
    who: "Administrador, Supervisor, Técnico (sólo las suyas), Fiscalizador.",
    how: [
      "Lista con buscador (código, cliente o dirección) y filtro por estado.",
      "Detalle con cliente, técnico asignado, contratista, tipo de servicio (FTTH/Radio Enlace), potencia óptica, validación EPP y marca de agua.",
      "Auditoría GPS comparando metraje declarado vs. validado.",
      "Botones de transición de estado: Asignada → En Progreso → Completada → Validada → Facturada.",
    ],
    pitch: "\"Toda la historia de una instalación está aquí: quién la hizo, cuándo, dónde, con qué fotos, cuántos metros declaró vs. los reales, si usó EPP, si la marca de agua de las fotos es correcta. Si llega un reclamo, el supervisor abre la orden y tiene todo en una pantalla.\"",
  },
  {
    icon: Network,
    name: "Diseño y Expansión de Red",
    purpose: "Gestión de solicitudes de expansión, análisis de factibilidad y planificación de activación de nodos.",
    who: "Administrador, Supervisor, Coordinador de Transporte de Datos, Líder de Territorio.",
    how: [
      "Lista de solicitudes con código (DR-AAAA###), sector geográfico, tipo y clientes potenciales.",
      "Estados: Solicitada → En Factibilidad → Aprobada → En Diseño → Activada (o Suspendida).",
      "KPIs de tendido planificado en km, clientes potenciales acumulados y nodos activados.",
      "Indica factibilidad (Sí/No/Parcial/Pendiente) y responsable de cada solicitud.",
    ],
    pitch: "\"Cuando un cliente corporativo pide servicio en un sector sin cobertura, la solicitud entra aquí. El equipo de red analiza factibilidad técnica (¿hay nodo cercano? ¿permisos municipales? ¿coordinación con CNEL?), planifica el tendido y aprueba la activación. Antes esto vivía en correos y reuniones; ahora es un flujo trazable.\"",
  },
  {
    icon: Calculator,
    name: "Calculadora de Materiales",
    purpose: "Reemplaza la planilla CALCULO DE MATERIALES TENDIDO.xlsx con un cálculo automático y verificación de stock.",
    who: "Administrador, Supervisor, Técnico.",
    how: [
      "Selecciona tipo de servicio (FTTH o Radio Enlace).",
      "Ingresa metros de drop/troncal, cantidad de rosetas, ONTs, routers y cajas NAP.",
      "El sistema calcula automáticamente los materiales necesarios con 5% de margen en fibra.",
      "Muestra desglose con código, cantidad, precio unitario, subtotal y validación de stock disponible.",
    ],
    pitch: "\"Antes el técnico llenaba un Excel a mano cada vez que iniciaba una instalación. Ahora ingresa los datos en 30 segundos y la calculadora devuelve la lista exacta con costos y alerta si algún material no tiene stock suficiente. Cero errores de cálculo, cero pedidos retrasados por falta de inventario.\"",
  },
  {
    icon: ShieldCheck,
    name: "Fiscalización con Vision AI",
    purpose: "Control de calidad automatizado de las evidencias fotográficas.",
    who: "Fiscalizador, Supervisor, Administrador.",
    how: [
      "Galería de fotos con dictamen IA: Validado (verde), Observación (rojo), Pendiente (gris).",
      "Cada foto incluye etiqueta del tipo (roseta, etiquetado, ONT, tensor) y % de confianza.",
      "Botones manuales para Validar o registrar Observación con texto libre.",
      "Filtros: Todos, Pendientes, Validados, Con observación.",
    ],
    pitch: "\"Antes un fiscalizador revisaba foto por foto manualmente. Ahora la IA pre-clasifica todo: el fiscalizador solo abre las marcadas como Observación. Reducimos el tiempo de fiscalización en más del 70%.\"",
    note: "En este PMV los dictámenes Vision AI están simulados. La siguiente fase integra un modelo real (Claude Vision o GPT-4o).",
  },
  {
    icon: MapPin,
    name: "Mapa GIS — Expansión de Red",
    purpose: "Visualización geoespacial del tendido de fibra y auditoría GPS.",
    who: "Fiscalizador, Supervisor, Administrador.",
    how: [
      "Marcadores verdes: instalaciones sin discrepancia. Rojos: con discrepancia >5%.",
      "Líneas verdes: trazas GPS validadas. Rojas: trazas con alerta.",
      "Filtros: Toda la red / Sólo alertas.",
      "Clic en marcador abre popup con datos y enlace al detalle.",
    ],
    pitch: "\"Cada técnico lleva el GPS encendido. Comparamos lo declarado con lo realmente recorrido. Si la diferencia es >5%, salta una alerta. Esto evita pagos por trabajo no realizado.\"",
  },
  {
    icon: Receipt,
    name: "Control de Facturación",
    purpose: "Control financiero del pago a contratistas con SLA, garantías y flujo de 3 firmas.",
    who: "Financiero, Administrador.",
    how: [
      "Tabla con contratista, tipo (Tendido / Última Milla), período, SLA, valor, garantía ácida, estado.",
      "Columna \"Firmas (3)\" con 3 círculos que se llenan en verde según avance: 1) Coordinador CC, 2) Gerente Procesos, 3) Contabilidad.",
      "Número de documento generado automáticamente (XXXMMAAAA).",
      "KPIs: valor total, % SLA pre-factura, % SLA facturación, borradores pendientes.",
      "Filtros por estado (Borrador, Aprobada, Rechazada).",
    ],
    pitch: "\"El proceso de pago a un contratista exige 3 firmas obligatorias: el Coordinador de Control de Calidad valida el informe, el Gerente de Procesos aprueba el monto y Contabilidad confirma la recepción. Aquí cada firma queda registrada con fecha y persona. Cero papeles perdidos.\"",
  },
  {
    icon: AlertTriangle,
    name: "No Conformidades",
    purpose: "Registro de errores documentales con seguimiento de corrección.",
    who: "Fiscalizador, Supervisor, Administrador.",
    how: [
      "Lista con OT, tipo de NC, descripción, contratista, fecha y estado.",
      "Botón \"Marcar corregida\" cuando el contratista subsana el error.",
      "Filtros: Todas / Abiertas / Corregidas. KPI de tasa de corrección.",
    ],
    pitch: "\"Cada error queda registrado con el contratista responsable. Sirve para retroalimentar, descontar de pre-factura y medir calidad histórica.\"",
  },
];

export default function Manual() {
  const handleExport = () => {
    try {
      exportManualPDF();
      toast.success("Manual PDF descargado");
    } catch {
      toast.error("No se pudo generar el manual PDF");
    }
  };

  const handleDownloadProvisioning = () => {
    const link = document.createElement("a");
    link.href = "/manuales/Manual_Aprovisionamiento_v1.pdf";
    link.download = "Manual_Aprovisionamiento_v1.pdf";
    link.click();
    toast.success("Manual de Aprovisionamiento descargado");
  };

  return (
    <Layout
      title="Manual de Usuario"
      subtitle="Guía completa por módulo"
      actions={
        <div className="flex items-center gap-2">
          <button
            onClick={handleDownloadProvisioning}
            className="flex items-center gap-2 text-sm px-4 py-2 rounded-lg font-semibold text-white"
            style={{ background: "#0A3747" }}
            data-testid="download-provisioning-manual"
          >
            <Zap size={15} /> Manual Aprovisionamiento
          </button>
          <button
            onClick={handleExport}
            className="btn-primary flex items-center gap-2 text-sm"
            data-testid="export-manual-pdf"
          >
            <FileDown size={15} /> Manual General PDF
          </button>
        </div>
      }
    >

      {/* Visión general */}
      <div className="kpi-card mb-6">
        <div className="text-[11px] uppercase font-mono text-gray-500 tracking-[0.18em] mb-1">
          1 · Visión general
        </div>
        <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "Chivo" }}>
          ¿Qué es Alfa-Field Connect?
        </h2>
        <p className="text-sm text-gray-700 leading-relaxed">
          Plataforma web que centraliza la operación de campo de TELEALFACOM (instalaciones de fibra óptica,
          asistencias técnicas, traslados y migraciones) desde la asignación de la orden de trabajo hasta la
          pre-facturación final del contratista.
        </p>
        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          <div className="border border-gray-200 rounded-md p-4 bg-gray-50">
            <div className="text-xs font-semibold uppercase tracking-wider text-red-600 mb-2">
              Problema que resuelve
            </div>
            <ul className="text-sm space-y-1.5 text-gray-700 list-disc list-inside">
              <li>Información dispersa en Excel, WhatsApp y reportes manuales.</li>
              <li>Tiempos largos entre ejecución y facturación al contratista.</li>
              <li>Falta de control automatizado sobre la calidad técnica.</li>
              <li>Discrepancias entre lo declarado y lo realmente instalado.</li>
            </ul>
          </div>
          <div className="border border-gray-200 rounded-md p-4 bg-[#F0FFCE]/40">
            <div className="text-xs font-semibold uppercase tracking-wider text-[#5A6B00] mb-2">
              Lo que aporta
            </div>
            <ul className="text-sm space-y-1.5 text-gray-700 list-disc list-inside">
              <li>Una sola fuente de verdad para todo el ciclo de la orden.</li>
              <li>Validación automática con Vision AI sobre fotos de campo.</li>
              <li>Auditoría GPS para verificar el metraje de fibra real.</li>
              <li>Indicadores ejecutivos en tiempo real para decisiones.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Roles */}
      <div className="kpi-card mb-6">
        <div className="text-[11px] uppercase font-mono text-gray-500 tracking-[0.18em] mb-1">
          2 · Roles del sistema
        </div>
        <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "Chivo" }}>
          ¿Quién usa qué?
        </h2>
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-y border-gray-200">
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-mono text-gray-500">Rol</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-mono text-gray-500">Para qué sirve</th>
                <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-mono text-gray-500">Módulos visibles</th>
              </tr>
            </thead>
            <tbody>
              {ROLES.map((r) => (
                <tr key={r.rol} className="border-b border-gray-100">
                  <td className="px-3 py-2.5 font-semibold">{r.rol}</td>
                  <td className="px-3 py-2.5 text-gray-700">{r.desc}</td>
                  <td className="px-3 py-2.5 text-xs text-gray-600 font-mono">{r.modulos}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Módulos */}
      <div className="text-[11px] uppercase font-mono text-gray-500 tracking-[0.18em] mb-1 mt-4">
        3 · Módulos paso a paso
      </div>
      <h2 className="text-xl font-bold mb-4" style={{ fontFamily: "Chivo" }}>
        ¿Cómo funciona cada módulo?
      </h2>

      <div className="space-y-5">
        {MODULES.map((m, i) => (
          <div key={m.name} className="kpi-card" data-testid={`manual-module-${i}`}>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg bg-[#0A3747] text-[#E0E621] flex items-center justify-center flex-shrink-0">
                <m.icon size={22} strokeWidth={1.6} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase font-mono text-gray-500 tracking-wider">
                    Módulo {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="text-lg font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>
                  {m.name}
                </h3>
                <p className="text-sm text-gray-700 mt-1.5">{m.purpose}</p>

                <div className="mt-4 grid md:grid-cols-3 gap-3">
                  <div>
                    <div className="text-[10px] uppercase font-mono text-gray-500 tracking-wider mb-1">
                      ¿Quién lo usa?
                    </div>
                    <div className="text-sm text-gray-700">{m.who}</div>
                  </div>
                  <div className="md:col-span-2">
                    <div className="text-[10px] uppercase font-mono text-gray-500 tracking-wider mb-1">
                      Cómo se usa
                    </div>
                    <ul className="text-sm space-y-1 text-gray-700">
                      {m.how.map((step, j) => (
                        <li key={j} className="flex items-start gap-2">
                          <span className="w-1 h-1 rounded-full bg-[#E0E621] mt-2 flex-shrink-0" />
                          <span>{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-4 border-l-2 border-[#E0E621] bg-gray-50 px-4 py-2.5 rounded-r">
                  <div className="text-[10px] uppercase font-mono text-gray-500 tracking-wider mb-0.5 flex items-center gap-1">
                    <Sparkles size={10} /> Cómo presentarlo
                  </div>
                  <p className="text-sm italic text-gray-800">{m.pitch}</p>
                </div>

                {m.note && (
                  <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                    <strong>Nota técnica:</strong> {m.note}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Estados de orden */}
      <div className="kpi-card mt-6">
        <div className="text-[11px] uppercase font-mono text-gray-500 tracking-[0.18em] mb-1">
          Anexo · Ciclo de vida de una orden
        </div>
        <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "Chivo" }}>
          Los 6 estados de una Orden de Trabajo
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {ESTADOS.map((s) => (
            <div key={s.n} className="border border-gray-200 rounded-md p-3.5">
              <div className="flex items-center gap-2.5 mb-1.5">
                <div className="w-7 h-7 rounded-full bg-[#E0E621] text-[#0A3747] font-bold flex items-center justify-center text-xs">
                  {s.n}
                </div>
                <span className="font-semibold text-sm">{s.e}</span>
              </div>
              <div className="text-xs text-gray-600 leading-relaxed">{s.d}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Guion de presentación */}
      <div className="kpi-card mt-6">
        <div className="text-[11px] uppercase font-mono text-gray-500 tracking-[0.18em] mb-1">
          4 · Guión de presentación
        </div>
        <h2 className="text-xl font-bold mb-3" style={{ fontFamily: "Chivo" }}>
          Demo en 15 minutos
        </h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-y border-gray-200">
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-mono text-gray-500 w-16">Min</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-mono text-gray-500 w-44">Pantalla</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-mono text-gray-500">Qué decir</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["0–1", "Login", "Mostrar los 5 roles. Entrar como Administrador."],
              ["1–4", "Dashboard", "Recorrer KPIs. Mostrar gráficos. Exportar el PDF."],
              ["4–6", "Agendamiento", "Calendario semanal. Navegar entre semanas."],
              ["6–9", "Órdenes → Detalle", "Cliente, técnico, auditoría GPS, evidencias."],
              ["9–11", "Fiscalización IA", "Validar una foto. Registrar observación en otra."],
              ["11–13", "Mapa GIS", "Mostrar alertas rojas. Filtrar sólo alertas."],
              ["13–14", "Facturación", "Tabla con SLA y garantías de contratistas."],
              ["14–15", "No Conformidades", "Marcar una corregida. Cierre de presentación."],
            ].map(([min, p, t]) => (
              <tr key={min} className="border-b border-gray-100">
                <td className="px-3 py-2 font-mono text-xs text-gray-600">{min}</td>
                <td className="px-3 py-2 font-semibold text-sm">{p}</td>
                <td className="px-3 py-2 text-sm text-gray-700">{t}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
